import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Checkbox } from "@headlessui/react";
import { CheckIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { 
  getUserInfo, 
  signIn, 
  requestOTP, 
  requestOTPForSignIn, 
  verifyOTPAndSignUp, 
  verifyOTPAndSignIn,
  changePasswordFirstLogin 
} from "../../services/authService";
import { useDispatch, useSelector } from "react-redux";
import { loginSuccess, setUser } from "../../store/slicers/authSlicer";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { normalizeUser } from "../../utils/normalizeUser";
import { useTranslation } from "react-i18next";
import BorderGlow from "../border-glow";
import PasswordChangeModal from "../PasswordChangeModal";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

export default function SignInForm() {
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [errors, setErrors] = useState({});
  const [authFlow, setAuthFlow] = useState("initial"); 
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpInfo, setOtpInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState(""); 
  const [isDark, setIsDark] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const inputRefs = useRef([]);
  const user = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleContinue = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingType("continue");
    
    const validationErrors = {};
    
    if (!email) {
      validationErrors.email = t('signIn.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      validationErrors.email = t('signIn.errors.invalidEmail');
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      setLoadingType("");
      return;
    }
    
    try {
      const userExists = await checkUserExists(email);
      
      if (userExists) {
        setAuthFlow("merchant");
      } else {
        try {
          await requestOTPForSignIn({ email });
          setOtpInfo({ email, flow: "signin" });
          setAuthFlow("customer-otp");
        } catch (error) {
          await requestOTP({ email });
          setOtpInfo({ email, flow: "signup" });
          setAuthFlow("customer-otp");
        }
      }
    } catch (error) {
      console.error("Continue error:", error);
      setErrors({ general: error.response?.data?.error || t('signIn.errors.requestError') });
    } finally {
      setIsLoading(false);
      setLoadingType("");
    }
  };

  const checkUserExists = async (email) => {
    try {
      return false; 
    } catch (error) {
      return false;
    }
  };

  const handlePasswordChange = async (passwordData) => {
    try {
      const res = await changePasswordFirstLogin(passwordData, tempToken);
      
      if (res.access_token) {
        setShowPasswordModal(false);
        setTempToken(null);
        
        dispatch(loginSuccess(res.access_token));
        
        try {
          const userResponse = await getUserInfo();
          const normalized = normalizeUser(userResponse?.data);
          dispatch(setUser(normalized));
        } catch (userError) {
          console.error("Error fetching user info:", userError);
        }
        
        navigate("/");
      }
    } catch (error) {
      console.error("Password change error:", error);
      throw error;
    }
  };

  const handleMerchantSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingType("signin");

    const validationErrors = {};
    if (!email) {
      validationErrors.email = t('signIn.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      validationErrors.email = t('signIn.errors.invalidEmail');
    }

    if (!password) {
      validationErrors.password = t('signIn.errors.passwordRequired');
    } else if (password.length < 6) {
      validationErrors.password = t('signIn.errors.passwordMinLength');
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsLoading(false);
      setLoadingType("");
      return;
    }

    const payload = { identifier: email, password, rememberMe };
    try {
      const res = await signIn(payload);
      
      if (res.requirePasswordChange) {
        setTempToken(res.tempToken);
        setShowPasswordModal(true);
        setIsLoading(false);
        setLoadingType("");
        return;
      }
      
      if (res.access_token) {
        dispatch(loginSuccess(res.access_token));
        const userResponse = await getUserInfo();
        const normalized = normalizeUser(userResponse?.data);
        dispatch(setUser(normalized));
        navigate("/");
      }
    } catch (error) {
      console.error("Sign-in error:", error);
      setErrors({ general: error.response?.data?.message || t('signIn.errors.invalidCredentials') });
    } finally {
      setIsLoading(false);
      setLoadingType("");
    }
  };

  const handleCustomerOTPSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingType("otp");
    
    const enteredOtp = otp.join('');
    
    if (enteredOtp.length !== 6) {
      setErrors({ otp: t('signIn.errors.invalidOtp') });
      setIsLoading(false);
      setLoadingType("");
      return;
    }
    
    try {
      let res;
      if (otpInfo.flow === "signup") {
        res = await verifyOTPAndSignUp({ otp: enteredOtp });
      } else {
        res = await verifyOTPAndSignIn({ otp: enteredOtp, email });
      }
      
      console.log("API Response:", res); 
      
      const token = res.token || res.access_token || res.data?.token;
      
      if (token) {
        dispatch(loginSuccess(token));
        
        try {
          const userInfo = res;
          if (userInfo?.customer) {
            const userRole = "b2c";
            
            dispatch(setUser({
              id: userInfo.customer.id,
              email: userInfo.customer.email,
              role: userRole,
              ...userInfo.customer
            }));
            console.log(setUser, "this is set user");
          } else if (userInfo?.data) {
            const userRole = userInfo.data.roleData?.name || "b2c";
            
            dispatch(setUser({
              id: userInfo.data.id,
              email: userInfo.data.email,
              role: userRole,
              ...userInfo.data
            }));
          } else {
            dispatch(setUser({
              email: email,
              role: "b2c"
            }));
          }
        } catch (userError) {
          console.error("Error processing user info:", userError);
          dispatch(setUser({
            email: email,
            role: "b2c"
          }));
        }
        
        navigate("/coming-soon");
      } else {
        setErrors({ general: t('signIn.errors.noToken') });
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setErrors({ 
        general: error.response?.data?.error || 
                error.response?.data?.message || 
                t('signIn.errors.invalidOtpCode')
      });
    } finally {
      setIsLoading(false);
      setLoadingType("");
    }
  };

  const handleKeyDown = (e) => {
    if (
      !/^[0-9]{1}$/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "Tab" &&
      !e.metaKey
    ) {
      e.preventDefault();
    }

    if (e.key === "Delete" || e.key === "Backspace") {
      const index = inputRefs.current.indexOf(e.target);
      if (index > 0) {
        setOtp((prevOtp) => [
          ...prevOtp.slice(0, index - 1),
          "",
          ...prevOtp.slice(index),
        ]);
        inputRefs.current[index - 1].focus();
      }
    }
  };

  const handleInput = (e) => {
    const { target } = e;
    const index = inputRefs.current.indexOf(target);
    if (target.value) {
      setOtp((prevOtp) => [
        ...prevOtp.slice(0, index),
        target.value,
        ...prevOtp.slice(index + 1),
      ]);
      if (index < otp.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    if (!new RegExp(`^[0-9]{${otp.length}}$`).test(text)) {
      return;
    }
    const digits = text.split("");
    setOtp(digits);
  };

  const Spinner = () => (
    <div className="flex items-center justify-center">
      <div className={`animate-spin rounded-full h-5 w-5 border-b-2 ${isDark ? 'border-white' : 'border-white'}`}></div>
    </div>
  );

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fa' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
    }`}>
      <div className="w-full max-w-md">
        <div className="absolute top-4 right-4">
          <button
            onClick={toggleLanguage}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              isDark 
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
            }`}
          >
            {i18n.language === 'en' ? 'فارسی' : 'English'}
          </button>
        </div>
   
        <div
          className={`transform transition-all duration-700 ${
            isAnimating
              ? "opacity-0 translate-y-10"
              : "opacity-100 translate-y-0"
          }`}
        >
          {authFlow === "initial" && (
              <BorderGlow>
          <div className={`rounded-2xl shadow-xl overflow-hidden border transition-colors duration-300 ${
              isDark 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-100'
            }`}>
              <div className="h-2 bg-gradient-to-r from-[#FF8B4C] via-[#FFC857] to-[#FFE9A0]"></div>

              <div className="px-8 py-10">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-4">
                    <div className="rounded-xl p-3 inline-block">
                      <img src="/logo.png" className="h-[35px]" alt="Logo" />
                    </div>
                  </div>
                  <h1 className={`text-3xl font-bold mb-2 transition-colors duration-300 ${
                    isDark ? 'text-white' : 'text-gray-800'
                  }`}>
                    {t('signIn.merchantLogin')}
                  </h1>
                  <p className={`transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {t('signIn.merchantSubtitle')}
                  </p>
                </div>

                <form onSubmit={handleMerchantSubmit} className="space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {t('signIn.email')}
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        dir="ltr"
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('signIn.emailPlaceholder')}
                        className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none focus:border-transparent transition-all duration-300 ${
                          isDark 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-gray-50 border-gray-200 text-gray-900'
                        }`}
                        required
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg
                          className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                        </svg>
                      </div>
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {t('signIn.password')}
                      </label>
                      {/* <Link
                        to="/forgot-password"
                        className={`text-sm font-medium transition-colors ${
                          isDark 
                            ? 'text-[#FF8B4C] hover:text-[#FFA06D]' 
                            : 'text-primary hover:text-[#FF8B4C]'
                        }`}
                      >
                        {t('signIn.forgotPassword')}
                      </Link> */}
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        dir="ltr"
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className={`w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none focus:border-transparent transition-all duration-300 pr-10 ${
                          isDark 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-gray-50 border-gray-200 text-gray-900'
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute inset-y-0 right-0 flex items-center pr-3 transition-colors ${
                          isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                        }`}
                        aria-label={showPassword ? t('signIn.hidePassword') : t('signIn.showPassword')}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="w-5 h-5" />
                        ) : (
                          <EyeIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center">
                    <Checkbox
                      checked={rememberMe}
                      onChange={setRememberMe}
                      className="group size-5 rounded-md rtl:ml-2  bg-primary p-1 ring-1 cursor-pointer ring-primary ring-inset focus:not-data-focus:outline-none data-checked:bg-white dark:data-checked:bg-black data-focus:outline data-focus:outline-offset-2 dark:data-focus:outline-black data-focus:outline-white"
                    >
                      <CheckIcon
                        className={`size-3 text-white fill-primary group-data-checked:fill-[#fff] ${
                          rememberMe ? "text-white" : ""
                        }`}
                      />
                    </Checkbox>
                    <div className="ml-1.5 text-sm">
                      <label className={`cursor-pointer transition-colors duration-300 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {t('signIn.rememberMe')}
                      </label>
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isLoading && loadingType === "signin"}
                      className="w-full py-3 px-4 bg-gradient-to-r from-primary to-[#ec68b5] hover:from-primary hover:to-[#ec68b5] text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isLoading && loadingType === "signin" ? (
                        <>
                          <Spinner />
                          {t('signIn.signingIn')}
                        </>
                      ) : (
                        t('signIn.signIn')
                      )}
                    </button>
                  </div>

                  {errors.general && (
                    <p className="text-sm text-red-600 dark:text-red-400 text-center">
                      {errors.general}
                    </p>
                  )}

                  <div className="flex gap-1 items-center w-full justify-between mt-6">
                    <div className={`w-[80px] h-[0.5px] ${isDark ? 'bg-gray-600' : 'bg-[#717B8E]'}`}></div>
                    <p className={`text-[14px] text-center ${isDark ? 'text-gray-400' : 'text-[#717B8E]'}`}>
                      {t('signIn.resellerPrompt')}
                    </p>
                    <div className={`w-[80px] h-[0.5px] ${isDark ? 'bg-gray-600' : 'bg-[#717B8E]'}`}></div>
                  </div>

                  <div>
                    <Link
                      to="/signup"
                      className={`w-full py-3 px-4 font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 inline-block text-center ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 focus:ring-gray-500' 
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
                      }`}
                    >
                      {t('signIn.registerNow')}
                    </Link>
                  </div>
                </form>
              </div>
            </div></BorderGlow>
          )}

          {authFlow === "customer-otp" && (
            <div className={`rounded-2xl shadow-xl overflow-hidden border transition-colors duration-300 ${
              isDark 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-100'
            }`}>
              <div className="h-2 bg-gradient-to-r from-[#FF8B4C] via-[#FFC857] to-[#FFE9A0]"></div>
              
              <div className="px-8 py-10">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-4">
                    <div className="bg-gradient-to-r from-[#FF8B4C] to-[#FFC857] rounded-xl p-3 inline-block">
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <h1 className={`text-3xl font-bold mb-2 transition-colors duration-300 ${
                    isDark ? 'text-white' : 'text-gray-800'
                  }`}>
                    {t('signIn.otpVerification')}
                  </h1>
                  <p className={`transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {t('signIn.otpMessage')} {email}
                  </p>
                </div>
                
                <form onSubmit={handleCustomerOTPSubmit} className="space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {t('signIn.enterOtp')}
                    </label>
                    <div className="flex justify-center gap-2">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          type="text"
                          maxLength={1}
                          value={digit}
                          onChange={handleInput}
                          onKeyDown={handleKeyDown}
                          onFocus={handleFocus}
                          onPaste={handlePaste}
                          ref={(el) => (inputRefs.current[index] = el)}
                          className={`w-12 h-12 text-center text-2xl font-medium rounded-lg focus:ring-2 focus:ring-primary focus:outline-none transition-colors duration-300 ${
                            isDark 
                              ? 'bg-gray-700 border-gray-600 text-white' 
                              : 'bg-white border border-gray-200 text-gray-900'
                          }`}
                        />
                      ))}
                    </div>
                    {errors.otp && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400 text-center">
                        {errors.otp}
                      </p>
                    )}
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isLoading && loadingType === "otp"}
                      className="w-full py-3 px-4 bg-gradient-to-r from-primary to-[#ec68b5] hover:from-primary hover:to-[#ec68b5] text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isLoading && loadingType === "otp" ? (
                        <>
                          <Spinner />
                          {t('signIn.verifying')}
                        </>
                      ) : (
                        t('signIn.verifyOtp')
                      )}
                    </button>
                  </div>

                  {errors.general && (
                    <p className="text-sm text-red-600 dark:text-red-400 text-center">
                      {errors.general}
                    </p>
                  )}

                  <div className="mt-4 text-center flex justify-between">
                    <button
                      type="button"
                      onClick={() => setAuthFlow("initial")}
                      className={`text-sm transition-colors ${
                        isDark 
                          ? 'text-[#FF8B4C] hover:text-[#FFA06D]' 
                          : 'text-primary hover:text-[#FF8B4C]'
                      }`}
                    >
                      {t('signIn.backToEmail')}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        if (otpInfo.flow === "signin") {
                          requestOTPForSignIn({ email });
                        } else {
                          requestOTP({ email });
                        }
                      }}
                      disabled={isLoading}
                      className={`text-sm transition-colors flex items-center gap-1 ${
                        isDark 
                          ? 'text-[#FF8B4C] hover:text-[#FFA06D]' 
                          : 'text-primary hover:text-[#FF8B4C]'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                          {t('signIn.resending')}
                        </>
                      ) : (
                        t('signIn.resendOtp')
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className={`mt-6 text-center text-xs transition-colors duration-300 ${
            isDark ? 'text-gray-500' : 'text-gray-500'
          }`}>
            © {new Date().getFullYear()} AFRH Merchant Portal. {t('signIn.allRightsReserved')}
          </div>
        </div>
      </div>

      {/* Password Change Modal for First Login */}
      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setTempToken(null);
        }}
        onSubmit={handlePasswordChange}
        isLoading={isLoading}
      />
    </div>
  );
}